import React from "react";
import { GridItem, GridContainer } from "./MerchantAndTerminals.styles";
import TextField from "../../textField/TextField";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

const OwnersFields = ({
  formValues,
  handleChange,
  handleBlur,
  handleOwnerChange,
  handleDeleteOwner,
  errors,
  touched,
  readOnly = false,
}) => {
  const { owner } = formValues;

  const sanitizeNoSpaceValue = (value) => value.replace(/\s+/g, "");

  const sanitizeOwnerPersonalPhone = (value) => {
    const hasLeadingPlus = value.startsWith("+");
    const digitsOnly = value.replace(/\D/g, "");
    return hasLeadingPlus ? `+${digitsOnly}` : digitsOnly;
  };

  const handleOwnerFieldChange = (index, field, value) => {
    let sanitizedValue = value;

    if (field === "id" || field === "taxpayer_id" || field === "postal_code") {
      sanitizedValue = sanitizeNoSpaceValue(value);
    } else if (field === "personal_phone") {
      sanitizedValue = sanitizeOwnerPersonalPhone(value);
    }

    handleOwnerChange(index, field, sanitizedValue);
  };

  return (
    <>
      {owner.map((ownerData, index) => (
        <Box
          key={index}
          sx={{ mb: 3, p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Typography variant="h6" color="primary">
              Owner {index + 1}
            </Typography>
            {owner.length > 1 && !readOnly && (
              <Tooltip title="Delete Owner" arrow>
                <IconButton
                  color="error"
                  onClick={() => handleDeleteOwner(index)}
                  disabled={readOnly}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
            {owner.length === 1 && (
              <Tooltip title="At least one owner is required" arrow>
                <span>
                  <IconButton color="error" disabled>
                    <DeleteIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>

          <GridContainer>
            <GridItem xs={12} md={6} lg={3} padding="0 15px 15px 0">
              <TextField
                id={`owner.${index}.first_name`}
                name={`owner.${index}.first_name`}
                fullWidth
                label="First Name"
                value={ownerData.first_name || ""}
                onChange={(e) =>
                  handleOwnerChange(index, "first_name", e.target.value)
                }
                onBlur={handleBlur}
                required
                maxLength={50}
                error={errors.owner?.[index]?.first_name}
                touched={touched.owner?.[index]?.first_name}
                disabled={readOnly}
                helperText="Owner's first name"
              />
            </GridItem>

            <GridItem xs={12} md={6} lg={3} padding="0 15px 15px 0">
              <TextField
                id={`owner.${index}.last_name`}
                name={`owner.${index}.last_name`}
                fullWidth
                label="Last Name"
                value={ownerData.last_name || ""}
                onChange={(e) =>
                  handleOwnerChange(index, "last_name", e.target.value)
                }
                onBlur={handleBlur}
                required
                maxLength={50}
                error={errors.owner?.[index]?.last_name}
                touched={touched.owner?.[index]?.last_name}
                disabled={readOnly}
                helperText="Owner's last name"
              />
            </GridItem>

            <GridItem xs={12} md={6} lg={3} padding="0 15px 15px 0">
              <TextField
                id={`owner.${index}.id`}
                name={`owner.${index}.id`}
                fullWidth
                label="ID"
                value={ownerData.id || ""}
                onChange={(e) =>
                  handleOwnerFieldChange(index, "id", e.target.value)
                }
                onBlur={handleBlur}
                required
                maxLength={20}
                inputProps={{ pattern: "\\S*" }}
                error={errors.owner?.[index]?.id}
                touched={touched.owner?.[index]?.id}
                disabled={readOnly}
                helperText="Owner's identification number"
              />
            </GridItem>

            <GridItem xs={12} md={6} lg={3} padding="0 15px 15px 0">
              <TextField
                id={`owner.${index}.taxpayer_id`}
                name={`owner.${index}.taxpayer_id`}
                fullWidth
                label="Taxpayer ID"
                value={ownerData.taxpayer_id || ""}
                onChange={(e) =>
                  handleOwnerFieldChange(index, "taxpayer_id", e.target.value)
                }
                onBlur={handleBlur}
                required
                maxLength={20}
                inputProps={{ pattern: "\\S*" }}
                error={errors.owner?.[index]?.taxpayer_id}
                touched={touched.owner?.[index]?.taxpayer_id}
                disabled={readOnly}
                helperText="Owner's taxpayer ID"
              />
            </GridItem>

            <GridItem xs={12} md={6} lg={3} padding="0 15px 15px 0">
              <TextField
                id={`owner.${index}.personal_email`}
                name={`owner.${index}.personal_email`}
                fullWidth
                label="Personal Email"
                value={ownerData.personal_email || ""}
                onChange={(e) =>
                  handleOwnerChange(index, "personal_email", e.target.value)
                }
                onBlur={handleBlur}
                required
                error={errors.owner?.[index]?.personal_email}
                touched={touched.owner?.[index]?.personal_email}
                disabled={readOnly}
                helperText="Owner's personal email"
              />
            </GridItem>

            <GridItem xs={12} md={6} lg={3} padding="0 15px 15px 0">
              <TextField
                id={`owner.${index}.personal_phone`}
                name={`owner.${index}.personal_phone`}
                fullWidth
                label="Personal Phone"
                value={ownerData.personal_phone || ""}
                onChange={(e) =>
                  handleOwnerFieldChange(index, "personal_phone", e.target.value)
                }
                onBlur={handleBlur}
                required
                maxLength={20}
                inputProps={{
                  pattern: "^\\+?[0-9]*$",
                  inputMode: "tel",
                }}
                error={errors.owner?.[index]?.personal_phone}
                touched={touched.owner?.[index]?.personal_phone}
                disabled={readOnly}
                helperText="Owner's personal phone"
              />
            </GridItem>

            <GridItem xs={12} md={6} lg={3} padding="0 15px 15px 0">
              <TextField
                id={`owner.${index}.address`}
                name={`owner.${index}.address`}
                fullWidth
                label="Address"
                value={ownerData.address || ""}
                onChange={(e) =>
                  handleOwnerChange(index, "address", e.target.value)
                }
                onBlur={handleBlur}
                required
                maxLength={200}
                error={errors.owner?.[index]?.address}
                touched={touched.owner?.[index]?.address}
                disabled={readOnly}
                helperText="Owner's address"
              />
            </GridItem>

            <GridItem xs={12} md={6} lg={3} padding="0 15px 15px 0">
              <TextField
                id={`owner.${index}.city`}
                name={`owner.${index}.city`}
                fullWidth
                label="City"
                value={ownerData.city || ""}
                onChange={(e) =>
                  handleOwnerChange(index, "city", e.target.value)
                }
                onBlur={handleBlur}
                required
                maxLength={50}
                error={errors.owner?.[index]?.city}
                touched={touched.owner?.[index]?.city}
                disabled={readOnly}
                helperText="Owner's city"
              />
            </GridItem>

            <GridItem xs={12} md={6} lg={3} padding="0 15px 15px 0">
              <TextField
                id={`owner.${index}.state`}
                name={`owner.${index}.state`}
                fullWidth
                label="State"
                value={ownerData.state || ""}
                onChange={(e) =>
                  handleOwnerChange(index, "state", e.target.value)
                }
                onBlur={handleBlur}
                required
                maxLength={30}
                error={errors.owner?.[index]?.state}
                touched={touched.owner?.[index]?.state}
                disabled={readOnly}
                helperText="Owner's state or province"
              />
            </GridItem>

            <GridItem xs={12} md={6} lg={3} padding="0 15px 15px 0">
              <TextField
                id={`owner.${index}.country`}
                name={`owner.${index}.country`}
                fullWidth
                label="Country"
                value={ownerData.country || ""}
                onChange={(e) =>
                  handleOwnerChange(index, "country", e.target.value)
                }
                onBlur={handleBlur}
                required
                maxLength={50}
                error={errors.owner?.[index]?.country}
                touched={touched.owner?.[index]?.country}
                disabled={readOnly}
                helperText="Owner's country"
              />
            </GridItem>

            <GridItem xs={12} md={6} lg={3} padding="0 15px 15px 0">
              <TextField
                id={`owner.${index}.postal_code`}
                name={`owner.${index}.postal_code`}
                fullWidth
                label="Postal Code"
                value={ownerData.postal_code || ""}
                onChange={(e) =>
                  handleOwnerFieldChange(index, "postal_code", e.target.value)
                }
                onBlur={handleBlur}
                required
                maxLength={20}
                inputProps={{ pattern: "\\S*" }}
                error={errors.owner?.[index]?.postal_code}
                touched={touched.owner?.[index]?.postal_code}
                disabled={readOnly}
                helperText="Owner's postal code"
              />
            </GridItem>
          </GridContainer>
        </Box>
      ))}

      {/* Error message for empty owner array */}
      {errors.owner && typeof errors.owner === "string" && touched.owner && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            border: 1,
            borderColor: "error.main",
            borderRadius: 1,
            backgroundColor: "error.light",
          }}
        >
          <Typography variant="body2" color="error">
            {errors.owner}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default OwnersFields;
